/**
 * The built-in Microsoft licence reference and the partner's sell prices for it, as the
 * marketplace function returns them. The reference (GUID, String ID, name, image, contract
 * terms) is the same for every partner; partners only set prices and distributors.
 */

export interface MicrosoftLicenceTerm {
  /** Key used in `termPricing`, e.g. `monthly_12`. */
  key: string;
  label: string;
  billingCycle: 'monthly' | 'annual';
  commitmentMonths: number;
}

export interface MicrosoftLicenceDistributor {
  id: string;
  label: string;
}

export type MicrosoftLicenceKind = 'seat' | 'cloudPc' | 'storage' | 'trialFree';

export interface MicrosoftLicence {
  /** The GUID Microsoft Graph returns as `subscribedSku.skuId`. */
  skuId: string;
  /** The String ID Graph returns as `subscribedSku.skuPartNumber`, e.g. SPB. */
  skuPartNumber: string;
  name: string;
  kind: MicrosoftLicenceKind;
  storefront: boolean;
  image: string;
  subCategory: string;
}

export interface MicrosoftLicenceReference {
  terms: MicrosoftLicenceTerm[];
  distributors: MicrosoftLicenceDistributor[];
  licences: MicrosoftLicence[];
}

/** Term key to the price the customer pays. */
export type TermPrices = Record<string, number>;

export interface PartnerMicrosoftLicencePricing {
  /** Blob version to save against. Null until the partner first saves. */
  version: string | null;
  defaultDistributor: string | null;
  /** Distributor id to that distributor's prices, keyed by licence GUID. */
  distributors: Record<string, { prices: Record<string, TermPrices> }>;
  /** Licence GUID to the description customers see. Missing until one is saved. */
  descriptions?: Record<string, string>;
  updatedAt?: string | null;
}

export interface ResolvedTermPrice {
  price: number;
  source: 'manual' | 'list';
  listPrice: number | null;
}

export interface ResolvedLicencePrice {
  skuId: string;
  distributor: string | null;
  distributorSource: 'licence' | 'customer' | 'partner' | 'none';
  /** Priced terms only. */
  terms: Record<string, ResolvedTermPrice>;
}

export interface CustomerMicrosoftLicenceRow {
  skuId: string;
  skuPartNumber: string;
  name: string;
  storefront: boolean;
  /** This customer's distributor for the licence. Null means the customer's default. */
  distributor: string | null;
  /** This customer's manual prices. */
  prices: TermPrices;
  /** Each distributor's list prices for the licence. */
  listPrices: Record<string, TermPrices>;
  /** What the customer pays now, worked out by the marketplace function. */
  resolved: ResolvedLicencePrice;
}

export interface CustomerMicrosoftLicencePricing {
  version: string | null;
  /** Null means the partner's default distributor. */
  defaultDistributor: string | null;
  partnerDefaultDistributor: string | null;
  licences: CustomerMicrosoftLicenceRow[];
}

export interface CustomerMicrosoftLicenceSetting {
  distributor: string | null;
  prices: TermPrices;
}

/** A save was refused because someone else saved first. */
export class PricingConflictError extends Error {}

export type ParsedPrice = { valid: true; value: number | null } | { valid: false };

/** A price box: blank means not priced; anything else must be an amount above zero, to the cent. */
export function parsePrice(text: string): ParsedPrice {
  const trimmed = text.trim();
  if (!trimmed) return { valid: true, value: null };
  if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) return { valid: false };
  const value = Number(trimmed);
  return value > 0 ? { valid: true, value } : { valid: false };
}

export function priceText(value: number | null | undefined): string {
  return typeof value === 'number' && value > 0 ? String(value) : '';
}

export function formatPrice(value: number): string {
  return `$${value.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function distributorLabel(reference: MicrosoftLicenceReference, id: string | null | undefined): string {
  return reference.distributors.find((distributor) => distributor.id === id)?.label ?? 'No distributor';
}

/** Term key to the text in its price box. */
export type TermPriceText = Record<string, string>;

/** Price boxes to prices, or the term keys whose text isn't a valid price. */
export function toTermPrices(text: TermPriceText): { prices: TermPrices; invalid: string[] } {
  const prices: TermPrices = {};
  const invalid: string[] = [];
  for (const [term, value] of Object.entries(text)) {
    const parsed = parsePrice(value);
    if (!parsed.valid) invalid.push(term);
    else if (parsed.value !== null) prices[term] = parsed.value;
  }
  return { prices, invalid };
}

export function toTermPriceText(prices: TermPrices | undefined): TermPriceText {
  const text: TermPriceText = {};
  for (const [term, value] of Object.entries(prices ?? {})) text[term] = priceText(value);
  return text;
}

/** Distributor id, then licence GUID, to the text in each term's price box. */
export type PartnerPriceDraft = Record<string, Record<string, TermPriceText>>;

/**
 * The partner's saved price lists as price-box text, for the seat licences the reference still
 * sells. Prices for any other licence are left out, so the next save removes them, and named in
 * `retired` so the partner is told. The function refuses a save that prices a licence it can't sell.
 */
export function toPartnerPriceDraft(
  reference: MicrosoftLicenceReference,
  pricing: PartnerMicrosoftLicencePricing,
): { draft: PartnerPriceDraft; retired: string[] } {
  const seatIds = new Set(reference.licences.filter((licence) => licence.kind === 'seat').map((licence) => licence.skuId));
  const names = new Map(reference.licences.map((licence) => [licence.skuId, licence.name]));
  const draft: PartnerPriceDraft = {};
  const retired = new Set<string>();

  for (const distributor of reference.distributors) {
    draft[distributor.id] = {};
    for (const [skuId, prices] of Object.entries(pricing.distributors[distributor.id]?.prices ?? {})) {
      if (seatIds.has(skuId)) draft[distributor.id][skuId] = toTermPriceText(prices);
      else retired.add(names.get(skuId) ?? skuId);
    }
  }
  return { draft, retired: Array.from(retired).sort((a, b) => a.localeCompare(b, 'en')) };
}

/**
 * For each priced seat licence, the other priced licences Microsoft gives the same name. Customers
 * see a licence by its name alone, so a priced pair looks like one product listed twice.
 */
export function sameNamedPricedLicences(
  licences: readonly MicrosoftLicence[],
  isPriced: (skuId: string) => boolean,
): Map<string, MicrosoftLicence[]> {
  const byName = new Map<string, MicrosoftLicence[]>();
  for (const licence of licences) {
    if (licence.kind !== 'seat' || !isPriced(licence.skuId)) continue;
    const key = licence.name.trim().toLowerCase();
    byName.set(key, [...(byName.get(key) ?? []), licence]);
  }

  const sameNamed = new Map<string, MicrosoftLicence[]>();
  for (const group of Array.from(byName.values())) {
    if (group.length < 2) continue;
    for (const licence of group) sameNamed.set(licence.skuId, group.filter((other) => other.skuId !== licence.skuId));
  }
  return sameNamed;
}

/** Whether any term has a valid price in the box text. */
export function hasPrice(text: TermPriceText | undefined): boolean {
  return Object.keys(toTermPrices(text ?? {}).prices).length > 0;
}

/** Case-insensitive match on name or String ID; every word of the query must appear. */
export function matchesLicenceQuery(licence: Pick<MicrosoftLicence, 'name' | 'skuPartNumber'>, query: string): boolean {
  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const haystack = `${licence.name} ${licence.skuPartNumber}`.toLowerCase();
  return words.every((word) => haystack.includes(word));
}
